import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Col, Form, Input, Modal, Row, Image, Spin, message } from 'antd';
import { dateTimeDisplayFormat, formatDisplayDate, ModalWidth, ROW_GUTTER } from '@/config';
import { formatCurrency } from '@/helpers/format';
import { GenderType } from '@/constants';
import { ConektoUserDetailModel } from '@/__generated';
import { apiSdk } from '@/httpClient';

type Props = {
  passengerId: string;
  onClose: () => void;
};

export default function PassengerInfoModal({ passengerId, onClose }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [passengerDetail, setPassengerDetail] = React.useState<ConektoUserDetailModel>();

  React.useEffect(() => {
    if (passengerId) {
      apiSdk.LianeUserManagementService.getDetails({ id: passengerId })
        .then(res => {
          setPassengerDetail(res);
        })
        .catch(message.error)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [passengerId]);

  React.useEffect(() => {
    form.setFieldsValue(passengerDetail);
  }, [passengerDetail]);
  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.large}
      style={{ top: 10 }}
      className="nta-custom-form"
      title={t('Passenger Info')}
      forceRender={true}
      cancelText={t('Close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <Spin spinning={loading}>
        <Form form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10 }}>
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>

          <Row gutter={ROW_GUTTER}>
            <Col md={12}>
              <Form.Item label={'User name'} name="userName">
                <Input disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={t('base.FirstName')} name="firstName">
                <Input disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={t('base.LastName')} name="lastName">
                <Input disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={t('base.Gender')}>
                <Input disabled className="input-value-color" value={GenderType.__getValue(passengerDetail?.gender)} />
              </Form.Item>
              <Form.Item label={t('base.DateOfBirth')}>
                <Input disabled className="input-value-color" value={formatDisplayDate(passengerDetail?.dateOfBirth)} />
              </Form.Item>

              <Form.Item label={t('base.PhoneCode')} name="phoneCode">
                <Input disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={t('base.PhoneNumber')} name="phoneNumber">
                <Input disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={'  '} colon={false}>
                <Checkbox value={passengerDetail?.phoneNumberConfirmed}>{t('passengerAppUser_PhoneNumberConfirmed')}</Checkbox>
              </Form.Item>

              <Form.Item label={t('base.Email')} name="emailAddress">
                <Input disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={'  '} colon={false}>
                <Checkbox value={passengerDetail?.emailConfirmed}>{t('passengerAppUser_EmailConfirmed')}</Checkbox>
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item label={'Current wallet balance'}>
                <Input disabled className="input-value-color" value={formatCurrency(passengerDetail?.currentWalletBalance)} />
              </Form.Item>
              <Form.Item label={'Last login'}>
                <Input disabled className="input-value-color" value={dateTimeDisplayFormat.dateTime(passengerDetail?.lastLogin)} />
              </Form.Item>
              <Form.Item label={t('Bio')} name="bio">
                <Input.TextArea disabled className="input-value-color" />
              </Form.Item>
              <Form.Item label={t('Profile image')}>
                <Image width={100} src={passengerDetail?.imgUrl} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
